import React, { useEffect, useState } from 'react';
import { LayerSelect, Layer } from 'components';
import { useLayers } from 'hooks/useLayers';
import { ICategory, ILayers, ISubTheme, ITheme } from 'contexts/@types/Layers';
import * as Styled from './styles';
import { useTranslation } from 'react-i18next';
import { clearText } from 'utils/clearText';
import * as RiIcons from 'react-icons/ri';
import { useMap } from 'hooks/useMap';
import { InputSlider } from 'components';
import { TextInput } from 'components/TextInput';
import { RiMenuSearchLine } from 'react-icons/ri';
import { CustomTooltip } from 'components/Tooltip';
interface ILayersProps {
  handleChangeTabIndex: (index: number, text: string, icon?: React.ReactNode) => void;
}

type ThemeItem =
  | { type: 'layer'; order?: number | null; index: number; layer: ILayers }
  | { type: 'subtheme'; order?: number | null; index: number; subTheme: ISubTheme };

const getThemeItemOrder = (item: ThemeItem) => item.order ?? Number.POSITIVE_INFINITY;

const getOrderedThemeItems = (theme: ITheme): ThemeItem[] => {
  const items: ThemeItem[] = [
    ...(theme.layers ?? []).map((layer, index): ThemeItem => ({
      type: 'layer',
      order: layer.ord_camada,
      index,
      layer,
    })),
    ...(theme.subtema ?? []).map((subTheme, index): ThemeItem => ({
      type: 'subtheme',
      order: subTheme.ord_subtema,
      index,
      subTheme,
    })),
  ];

  return items.sort((firstItem, secondItem) => {
    const firstOrder = getThemeItemOrder(firstItem);
    const secondOrder = getThemeItemOrder(secondItem);

    if (firstOrder !== secondOrder) return firstOrder - secondOrder;
    if (firstItem.type === secondItem.type) return firstItem.index - secondItem.index;

    return firstItem.type === 'layer' ? -1 : 1;
  });
};

export function Layers({ handleChangeTabIndex }: ILayersProps) {
  const [query, setQuery] = useState<string>('');
  const {
    categoryList,
    handleOpenCategory,
    handleOpenTheme,
    handleOpenSubTheme,
    setSubThemeGraph,
    handleRemoveLayer,
    layersActived,
    handleCountLayersActived,
  } = useLayers();

  const {
    handleRemoveOverlayLayer,
    setOpenLayerFeaturesModal,
    setOpenLayerDetailsModal,
    layerActiveFeature,
    addedLayers,
    handleChangeOpacityLayer,
    getOpacityLayer,
    handleChangeVisibility
  } = useMap();

  const { t: translation } = useTranslation();

  const [filteredCategories, setFilteredCategories] = useState<Set<number>>(new Set());
  const [filteredTheme, setFilteredTheme] = useState<Set<number>>(new Set());
  const [filteredSubTheme, setFilteredSubTheme] = useState<Set<number>>(new Set());
  const [filteredLayer, setFilteredLayer] = useState<Set<number>>(new Set());
  const [layerVisibility, setLayerVisibility] = useState<Record<string, boolean>>({});
  const [layerOpacity, setLayerOpacity] = useState<Record<string, number>>({});

 const toggleLayerVisibility = (uuid: string) => {
    const isVisible = layerVisibility[uuid] ?? true;
    const newVisible = !isVisible;

    setLayerVisibility(prev => ({
      ...prev,
      [uuid]: newVisible
    }));

    handleChangeOpacityLayer(uuid, !isVisible ? 1 : 0);
    handleChangeVisibility(uuid,newVisible);
  };
  

  const handleOpacityChange = (uuid: string, opacity: number) => {
    setLayerVisibility(prev => ({
      ...prev,
      [uuid]: opacity !== 0
    }));

    setLayerOpacity(prev => ({
      ...prev,
      [uuid]: opacity
    }));

    handleChangeOpacityLayer(uuid, opacity);
    handleChangeVisibility(uuid,opacity !== 0);
  };

  const renderLayers = (layers: ILayers[], category_name: string) => {
    return layers.map(layer => {
      if (query && !filteredLayer.has(layer.id)) return;

      return (
        <Layer
          key={'layer_' + layer.layertitle}
          layertitle={layer.layer_alias ?? layer.layertitle}
          layer={layer}
          is_new={!!layer.nova_camada}
          handleChangeTabIndex={handleChangeTabIndex}
          checked={false}
          nm_category={category_name}
        />
      );
    });
  };

  useEffect(() => {
    handleCountLayersActived();
  }, []);

  const updateStates = (category_id: number, theme_id?: number, subtheme_id?: number, layer_id?: number) => {
    setFilteredCategories(prevState => prevState.add(category_id));
    if (theme_id) setFilteredTheme(prevState => prevState.add(theme_id));
    if (subtheme_id) setFilteredSubTheme(prevState => prevState.add(subtheme_id));
    if (layer_id) setFilteredLayer(prevState => prevState.add(layer_id));
  };

  useEffect(() => {
    setFilteredCategories(new Set());
    setFilteredTheme(new Set());
    setFilteredSubTheme(new Set());
    setFilteredLayer(new Set());
    if (!query) return;

    categoryList.forEach(category => {
      category.temas.forEach(theme => {
        theme.subtema?.forEach(subTheme => {
          subTheme.layers?.forEach(layer => {
            if (clearText(layer.layertitle).includes(clearText(query)) || clearText(layer.layer_alias).includes(clearText(query))) {
              updateStates(category.id_categoria, theme.id_tema, subTheme.id_subtema, layer.id);
            }
          });
        });
        theme.layers?.forEach(layer => {
          if (clearText(layer.layertitle).includes(clearText(query)) || clearText(layer.layer_alias).includes(clearText(query))) {
            setFilteredCategories(prevState => prevState.add(category.id_categoria));
            setFilteredTheme(prevState => prevState.add(theme.id_tema));
            setFilteredLayer(prevState => prevState.add(layer.id));
          }
        });
      });
    });
  }, [query]);

  const handleOpenGraph = (subtheme: ISubTheme) => {
    setSubThemeGraph(subtheme);
    handleChangeTabIndex(3, translation('mapPage.sideBar.navigationBottomBar.charts'), <RiIcons.RiBarChart2Fill />);
  };

  function clearCategory(category: ICategory) {
    category.temas.forEach(theme => {
      clearTheme(theme);
    })
  }

  function clearTheme(theme: ITheme) {
    theme.layers?.forEach(layer => {
      clearLayers(layer);
    })
    theme.subtema?.forEach(subTheme => {
      clearSubTheme(subTheme);
    })
  }

  function clearSubTheme(subTheme: ISubTheme) {
    subTheme.layers?.forEach(layer => {
      clearLayers(layer);
    })
  }

  function clearLayers(layer: ILayers) {
    if (
      layersActived.find(
        (layerActived: ILayers) =>
          layerActived.id === layer.id && (layerActived.layer_filter_id === layer.layer_filter_id) && layer.visible,
      )
    ) {
      handleRemoveLayer(layer);
      handleRemoveOverlayLayer(layer.layertitle + (layer.layer_filter_id || '') + layer.layername);
      const uuidLayer = layer.layertitle + (layer.layer_filter_id || '') + layer.layername;

      if (uuidLayer === layerActiveFeature) {
        setOpenLayerFeaturesModal(false);
        setOpenLayerDetailsModal(false);
      }
    }
  }

  function removeFileExtension(Title: string) {
    return Title.replace(/\.(kml|geojson|zip|shp)$/i, '');
  }

  return (
    <Styled.Wrapper>

      <div className="text-input-wrapper">
        <TextInput
          icon={(<RiMenuSearchLine size={24} />)}
          placeholder={'Buscar nas camadas'}
          value={query}
          onChange={setQuery}
        />
      </div>

      {(!query || filteredCategories.size !== 0) &&
        categoryList.map((category: ICategory, indexCategory: number) => {
          if (query && !filteredCategories.has(category.id_categoria)) return;

          return (
            <div key={`category_${category.id_categoria}_wrapper`}>
              {category.id_categoria === 1000 && <Styled.Separator />}
              <LayerSelect
                id={'category_' + category.id_categoria}
                name={category.nm_categoria}
                key={'category_' + category.id_categoria}
                qtdOpened={category.qtdOpenend}
                opened={!!query || !!category.opened}
                onChange={() => handleOpenCategory(!category.opened, indexCategory)}
                clear={() => clearCategory(category)}
                disabled={!category.temas || category.temas?.length === 0}
                camadas_filtradas={category.camadas_pesquisadas}
              >
                {category.temas.map((theme: ITheme, indexTheme: number) => {
                  if (!theme.visivel) return;
                  if (query && !filteredTheme.has(theme.id_tema)) return;

                  return (
                    <LayerSelect
                      id={'theme_' + theme.id_tema}
                      name={theme.nm_tema}
                      key={'theme_' + theme.id_tema}
                      qtdOpened={theme.qtdOpenend}
                      opened={!!query || !!theme.opened}
                      onChange={() => handleOpenTheme(!theme.opened, indexCategory, indexTheme)}
                      clear={() => clearTheme(theme)}
                      disabled={
                        (!theme.subtema || theme.subtema?.length === 0) && (!theme.layers || theme.layers?.length === 0)
                      }
                    >
                      {getOrderedThemeItems(theme).map(item => {
                        if (item.type === 'layer') {
                          return renderLayers([item.layer], theme.nm_tema);
                        }

                        const { subTheme, index: indexSubTheme } = item;

                        if (!subTheme.visivel) return;
                        if (query && !filteredSubTheme.has(subTheme.id_subtema)) return;

                        return (
                          <LayerSelect
                            id={'sub_theme_' + subTheme.id_subtema}
                            name={subTheme.nm_subtema}
                            key={'sub_theme_' + subTheme.id_subtema}
                            qtdOpened={subTheme.qtdOpenend}
                            opened={!!query || !!subTheme.opened}
                            is_new={false}
                            onChange={() => handleOpenSubTheme(!subTheme.opened, indexCategory, indexTheme, indexSubTheme)}
                            has_graph={category.id_categoria === 1}
                            handleOpenGraph={() => handleOpenGraph(subTheme)}
                            clear={() => clearSubTheme(subTheme)}
                            disabled={!subTheme.layers || subTheme.layers?.length === 0}
                          >
                            {subTheme.layers && subTheme.layers?.length
                              ? renderLayers(subTheme.layers, subTheme.nm_subtema)
                              : ''}
                          </LayerSelect>
                        );
                      })}
                    </LayerSelect>
                  );
                })}
              </LayerSelect>
            </div>
          );
        })}

      <Styled.Separator />
      {(!query || addedLayers.some(l => clearText(l.layername).includes(query))) &&
        <>
          <Styled.ExternalLayersHeader>
            Camadas externas:
          </Styled.ExternalLayersHeader>
          {addedLayers.length === 0 && (
            <Styled.EmptyExternalLayers>
              Nenhuma camada externa adicionada
            </Styled.EmptyExternalLayers>
          )}

          {addedLayers.map(layer => {
            if (query && !clearText(layer.layername).includes(query)) return;
            const uuid =
              layer.layertitle +
              (layer.layer_filter_id || '') +
              layer.layername;

            const visible = layerVisibility[uuid] ?? getOpacityLayer(uuid) > 0;
            const opacity = layerOpacity[uuid] ?? getOpacityLayer(uuid);

            return (
              <React.Fragment key={uuid}>
                <Styled.ExternalLayerItem>
                  <Styled.ExternalLayerName>
                    <Styled.Button
                      label={translation('mapPage.sideBar.layers.showHide')}
                      onClick={() => toggleLayerVisibility(uuid)}
                    >
                      {visible ? (
                        <RiIcons.RiEyeFill size={20} color="#000" />
                      ) : (
                        <RiIcons.RiEyeOffFill size={20} color="#000" />
                      )}
                    </Styled.Button>
                    <CustomTooltip title={removeFileExtension(layer.layer_alias ?? layer.layertitle)} placement="top" disableInteractive>
                      <a>{removeFileExtension(layer.layer_alias ?? layer.layertitle)}</a>
                    </CustomTooltip>
                  </Styled.ExternalLayerName>

                  <Styled.ExternalLayerActions>
                    <RiIcons.RiDeleteBin6Line
                      size={18}
                      onClick={() => handleRemoveOverlayLayer(uuid)}
                    />
                  </Styled.ExternalLayerActions>
                </Styled.ExternalLayerItem>

                <Styled.Content>
                  <InputSlider
                    label={''}
                    min={0}
                    max={1}
                    step={0.01}
                    value={opacity}
                    onChange={({ target }) =>
                      handleOpacityChange(uuid, Number(target.value))
                    }
                  />
                </Styled.Content>

                <br />
              </React.Fragment>
            );
          })}
        </>
      }

      {query && filteredCategories.size === 0 && !addedLayers.some(l => clearText(l.layername).includes(query)) && (
        <div className="notFound">
          <h2>{translation('mapPage.sideBar.layers.notFound')}</h2>
        </div>
      )}
    </Styled.Wrapper>

  );
}
